import React, { useState } from "react";
import { AlertCircle, ArrowUpRight, ArrowLeft } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";

export default function AdminLogin() {
  const { loginWithGoogle, authError, loading, connectionStatus } = useAdminAuth();
  const [inProgress, setInProgress] = useState(false);

  const handleGoogleSignIn = async () => {
    setInProgress(true);
    try {
      await loginWithGoogle();
    } catch (e) {
      console.warn("Login failed:", e);
    } finally {
      setInProgress(false);
    }
  };

  return (
    <div className="relative min-h-[100svh] w-full overflow-hidden bg-[#030305] text-slate-100 flex items-center justify-center p-4 sm:p-6 font-sans selection:bg-primary selection:text-white">
      {/* Background ambient glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] max-w-[700px] max-h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(67,106,231,0.15), transparent 60%)" }}
      />

      <div className="relative z-10 w-full max-w-md">
        {/* Modern Clean Card */}
        <div className="relative border border-white/10 bg-[#07080f]/90 backdrop-blur-2xl p-8 sm:p-10 shadow-2xl rounded-2xl space-y-8">
          {/* Logo & Headline */}
          <div className="text-center space-y-3">
            <img
              src="https://databaseimgberry.edgeone.app/berry_113.png"
              alt="Berry Logo"
              className="w-14 h-14 mx-auto rounded-full object-cover shadow-xl shadow-primary/20"
            />

            <div className="space-y-1 pt-1">
              <h1 className="font-display font-bold text-2xl sm:text-3xl text-white tracking-tight">
                Admin Panel
              </h1>
              <p className="text-xs sm:text-sm text-white/50 leading-relaxed">
                Sign in with your authorized Google account to access BMA CORP Control Center.
              </p>
            </div>
          </div>

          {/* Error Alert */}
          {authError && (
            <div className="p-4 border border-destructive/30 bg-destructive/10 text-destructive text-xs rounded-xl flex items-start gap-3">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <div className="space-y-1">
                <span className="font-bold block">Access Denied</span>
                <p className="text-white/80 leading-relaxed text-[11px]">{authError}</p>
              </div>
            </div>
          )}

          {/* Google Sign In Button */}
          <div className="space-y-4">
            <button
              onClick={handleGoogleSignIn}
              disabled={loading || inProgress}
              className="group w-full h-12 bg-primary text-white font-medium text-xs sm:text-sm rounded-xl flex items-center justify-center gap-3 hover:bg-white hover:text-black transition-all duration-200 disabled:opacity-50 active:scale-[0.99] shadow-lg shadow-primary/20"
            >
              {loading || inProgress ? (
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-4 h-4 shrink-0 fill-current" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                  </svg>
                  <span>Sign in with Google</span>
                  <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </>
              )}
            </button>

            {/* Connection Ping */}
            <div className="flex items-center justify-between pt-3 border-t border-white/5 text-[11px] text-white/40 font-mono-tech">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span>Database Connected</span>
              </div>
              <span>{connectionStatus.latency || 28}ms latency</span>
            </div>
          </div>

          {/* Return link */}
          <div className="pt-2 text-center">
            <a
              href="https://berrymodapk.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors"
            >
              <ArrowLeft size={13} />
              <span>Visit Main Website (berrymodapk.com)</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
